import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Icon } from './Icon';
import { todayIsoUtc } from '@/lib/calendar';

interface DatePickerProps {
  value: string | null;
  onChange: (iso: string | null) => void;
  /**
   * When true and `value` is null, today's date is shown as the input's
   * placeholder text only — the control stays fully controlled and does NOT
   * emit today's date on its own. The caller is responsible for committing a
   * value if they want it persisted.
   */
  defaultToToday?: boolean;
  min?: string;
  max?: string;
  id?: string;
  clearable?: boolean;
  className?: string;
}

// Styled native <input type="date">. Emits `yyyy-mm-dd` or null (empty input).
export function DatePicker({
  value,
  onChange,
  defaultToToday = false,
  min,
  max,
  id,
  clearable = false,
  className,
}: DatePickerProps) {
  const { t } = useTranslation();
  return (
    <div className={cn('relative flex items-center', className)}>
      <Icon
        name="calendar"
        size={15}
        className="pointer-events-none absolute left-3 text-muted"
      />
      <input
        id={id}
        type="date"
        value={value ?? ''}
        min={min}
        max={max}
        // Surfaces today's date as a hint when the caller opts in, without
        // mutating the value. Native date inputs ignore `placeholder` in most
        // browsers, so we also expose it via title for assistive tech.
        title={defaultToToday && value == null ? todayIsoUtc() : undefined}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className="h-11 w-full rounded-md border border-line bg-paper pl-9 pr-9 text-sm text-ink outline-none focus-visible:border-line-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
      />
      {clearable && value != null && (
        <button
          type="button"
          aria-label={t('common.remove')}
          onClick={() => onChange(null)}
          className="absolute right-3 inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted hover:bg-paper-2"
        >
          <Icon name="x" size={13} strokeWidth={2.2} />
        </button>
      )}
    </div>
  );
}
